import { Constants, isUKCConformationUnscheduledLevel } from "@eagerdog/constants";
import { IEventUKCConformationCount, IRunningOrder, IRunningOrderDogClass, IShow, IShowRing } from "@eagerdog/interfaces";

/**
 * Puts the judges and classes configured on a UKC Conformation show into its rings.
 *
 * The Edit Show form stores who judges what on `breed_group_judges`, `breed_judges`
 * and `show_elements[].judge_name`. The Judge Schedules tab reads those when it
 * opens; the builder has to do the same, otherwise a day whose rings were never
 * opened in that tab shows no judges, and a judge added from the builder gets an
 * empty column even though the show already says which breed groups they judge.
 *
 * Rules, matching the Judge Schedules tab:
 * - A configured judge missing from every ring is added to the first ring.
 * - A class is only added when no judge in any ring already has it, so classes a
 *   club moved by hand stay where they were put.
 * - Breed groups with no configured judge go to the Conformation element judge.
 * - Novice Puppy, Veteran, Total Dog and Total Junior never get a tile.
 * - Junior Showmanship is one tile with no level.
 */

type Counts = Map<string, IEventUKCConformationCount>; // key "breedGroup-breed"

interface Assignment {
  judge_name: string;
  judge_number: string;
  show_element: string;
  level: string;
  start_letter?: string;
  end_letter?: string;
  entries: number;
}

function judgeKey(name: string, number?: string): string {
  return `${name}||${number || ""}`;
}

function sumEntries(count: IEventUKCConformationCount): number {
  return (
    (count.male_count || 0) + (count.female_count || 0) +
    (count.champion_count || 0) + (count.grand_champion_count || 0) +
    (count.novice_puppy_count || 0) + (count.brace_count || 0) + (count.veteran_count || 0) +
    (count.alt_male_count || 0) + (count.alt_female_count || 0) +
    (count.alt_champion_count || 0) + (count.alt_grand_champion_count || 0) +
    (count.alt_novice_puppy_count || 0) + (count.alt_brace_count || 0) + (count.alt_veteran_count || 0)
  );
}

export function groupUKCConformationCounts(rows: IEventUKCConformationCount[] | undefined): Counts {
  const counts: Counts = new Map();
  (rows || []).forEach((row) => {
    if (!row?.breed_group || !row?.breed) return;
    const key = `${row.breed_group}-${row.breed}`;
    const existing = counts.get(key);
    if (!existing) {
      counts.set(key, { ...row });
      return;
    }
    (Object.keys(row) as (keyof IEventUKCConformationCount)[]).forEach((field) => {
      if (typeof row[field] === "number") {
        (existing as any)[field] = ((existing as any)[field] || 0) + (row[field] as number);
      }
    });
  });
  return counts;
}

function groupEntries(counts: Counts, breedGroup: string, breedSpecific: Set<string>, startLetter?: string, endLetter?: string): number {
  let total = 0;
  counts.forEach((count, key) => {
    const group = key.split("-")[0];
    if (group !== breedGroup) return;
    const breed = key.substring(group.length + 1);
    if (breedSpecific.has(breed)) return;
    if (startLetter && endLetter) {
      const first = breed[0]?.toUpperCase() ?? "A";
      if (first < startLetter || first > endLetter) return;
    }
    total += sumEntries(count);
  });
  return total;
}

function breedEntries(counts: Counts, breed: string): number {
  let total = 0;
  counts.forEach((count, key) => {
    const group = key.split("-")[0];
    if (key.substring(group.length + 1) === breed) total += sumEntries(count);
  });
  return total;
}

/** Everything the show says should be judged, with the judge for each. */
export function ukcConformationAssignments(show: IShow, counts: Counts): Assignment[] {
  const assignments: Assignment[] = [];
  const breedJudges = show.breed_judges || [];
  const groupJudges = show.breed_group_judges || [];
  const elements = show.show_elements || [];
  const breedSpecific = new Set(breedJudges.map((item) => item.breed));
  const conformationJudge = elements.find((se) => se.show_element === Constants.dog_class_element.conformation && se.judge_name);
  const fallback = conformationJudge
    ? { judge_name: conformationJudge.judge_name, judge_number: conformationJudge.judge_number || "" }
    : undefined;

  breedJudges.forEach((item) => {
    if (!item.judge_name) return;
    const entries = breedEntries(counts, item.breed);
    if (entries === 0) return;
    assignments.push({
      judge_name: item.judge_name,
      judge_number: item.judge_number || "",
      show_element: Constants.dog_class_element.conformation,
      level: item.breed,
      entries,
    });
  });

  const byGroup = new Map<string, typeof groupJudges>();
  groupJudges.forEach((item) => {
    const list = byGroup.get(item.breed_group) || [];
    list.push(item);
    byGroup.set(item.breed_group, list);
  });

  (Constants.ukc_breed_group_order as string[]).forEach((breedGroup) => {
    const configured = byGroup.get(breedGroup) || [];
    if (configured.length === 0) {
      const entries = groupEntries(counts, breedGroup, breedSpecific);
      if (entries === 0 || !fallback) return;
      assignments.push({ ...fallback, show_element: Constants.dog_class_element.conformation, level: breedGroup, entries });
      return;
    }
    configured.forEach((item) => {
      const judge = item.judge_name
        ? { judge_name: item.judge_name, judge_number: item.judge_number || "" }
        : fallback;
      if (!judge) return;
      const split = configured.length > 1 && item.start_letter && item.end_letter;
      assignments.push({
        ...judge,
        show_element: Constants.dog_class_element.conformation,
        level: breedGroup,
        start_letter: split ? item.start_letter : undefined,
        end_letter: split ? item.end_letter : undefined,
        entries: groupEntries(counts, breedGroup, breedSpecific, split ? item.start_letter : undefined, split ? item.end_letter : undefined),
      });
    });
  });

  const conformationElements = new Set<string>([
    Constants.dog_class_element.conformation,
    Constants.dog_class_element.conformation_altered,
  ]);
  const seen = new Set<string>();
  elements.forEach((se) => {
    if (!se.show_element || conformationElements.has(se.show_element)) return;
    const juniors = se.show_element === Constants.dog_class_element.junior_showmanship;
    const level = juniors ? "" : (se.level || "");
    if (!level && !juniors) return;
    if (isUKCConformationUnscheduledLevel(level)) return;
    const key = `${se.show_element}||${level}`;
    if (seen.has(key)) return;
    seen.add(key);
    const judge = se.judge_name ? { judge_name: se.judge_name, judge_number: se.judge_number || "" } : fallback;
    if (!judge) return;
    assignments.push({ ...judge, show_element: se.show_element, level, entries: 0 });
  });

  return assignments;
}

function sameClass(tile: IRunningOrderDogClass, assignment: Assignment): boolean {
  if (tile.type === Constants.running_order_dog_class_type.break) return false;
  if ((tile.show_element || "") !== assignment.show_element) return false;
  if (assignment.show_element === Constants.dog_class_element.junior_showmanship) return true;
  if ((tile.level || "") !== assignment.level) return false;
  if (assignment.start_letter && assignment.end_letter) {
    return tile.start_letter === assignment.start_letter && tile.end_letter === assignment.end_letter;
  }
  return true;
}

/**
 * Rings with every configured judge present and every configured class placed.
 * Returns null when nothing had to change.
 */
export function syncUKCConformationAssignments(
  show: IShow,
  counts: Counts,
  runningTimePerDog: number
): IShowRing[] | null {
  const rings: IShowRing[] = (show.rings || []).map((ring) => ({
    ...ring,
    running_order: (ring.running_order || []).map((judge) => ({ ...judge, dog_classes: [...(judge.dog_classes || [])] })),
  }));
  if (rings.length === 0) return null;

  const assignments = ukcConformationAssignments(show, counts);
  if (assignments.length === 0) return null;

  let changed = false;
  const judges = (): IRunningOrder[] => rings.flatMap((ring) => ring.running_order || []);
  const findJudge = (name: string, number: string) =>
    judges().find((judge) => judgeKey(judge.judge_name, judge.judge_number) === judgeKey(name, number));

  assignments.forEach((assignment) => {
    if (!findJudge(assignment.judge_name, assignment.judge_number)) {
      rings[0].running_order!.push({ judge_name: assignment.judge_name, judge_number: assignment.judge_number, dog_classes: [] });
      changed = true;
    }
  });

  assignments.forEach((assignment) => {
    if (judges().some((judge) => judge.dog_classes.some((tile) => sameClass(tile, assignment)))) return;
    const target = findJudge(assignment.judge_name, assignment.judge_number);
    if (!target) return;
    target.dog_classes.push({
      type: Constants.running_order_dog_class_type.dog_class,
      show_element: assignment.show_element,
      level: assignment.level,
      start_letter: assignment.start_letter,
      end_letter: assignment.end_letter,
      entries: assignment.entries,
      duration: runningTimePerDog,
      total_minutes: assignment.entries * runningTimePerDog,
    });
    changed = true;
  });

  return changed ? rings : null;
}
